import { useTranslation } from "react-i18next";
import { LoadingState } from "../states";
import placeholder from "../states/Placeholder.module.css";
import { PlaceholderText } from "../states/PlaceholderText";
import { SectionGrid } from "../layout/SectionLayout";
import { useSectionLayout } from "../layout/sectionLayoutContext";
import type { SectionLayoutMode } from "../layout/sectionLayoutContext";
import card from "../tools/ToolCard.module.css";
import styles from "./GroupCard.module.css";
import loadingStyles from "./GroupsLoading.module.css";

/** Sample text of about the length of a group's name and path, which come from the data rather than the translations. */
const sampleGroup = { path: "… › Product and Technology", name: "Platform Engineering", tool: "GitHub Copilot" };

/** How many cards the loading layout sketches in each layout: about one row's worth (the dense grid draws three to a row). */
const placeholderCardCount: Record<SectionLayoutMode, number> = { single: 2, loose: 2, dense: 3 };

/** The tools a card lists, sketched: about as many as most groups use. */
const placeholderToolCount = 3;

/**
 * One group card, sketched: the same parts as a loaded card at this layout's density, with sample text drawn as soft bars,
 * so the cards keep their size and the groups arrive in place. The first card holds the status (the spinner, or the error
 * when the groups could not be loaded) where the group's spend will be.
 *
 * @param props.mode - The layout, which sets how much a card shows.
 * @param props.status - The spinner or the error, for the first card only.
 * @param props.failed - Whether the status is an error, which takes the room it needs rather than one line.
 */
function PlaceholderGroupCard({ mode, status, failed = false }: { mode: SectionLayoutMode; status?: React.ReactNode; failed?: boolean }): React.JSX.Element {
  const { t } = useTranslation();
  const dense = mode === "dense";

  return (
    <div className={`${card.card} ${card.surface} ${styles.groupCard}`}>
      <div className={styles.top} aria-hidden="true">
        <div className={styles.identity}>
          <p className={styles.eyebrow}>
            <span className={styles.kind}>
              <PlaceholderText>{t("myGroups.kind.department")}</PlaceholderText>
            </span>
            <span className={styles.path}>
              <PlaceholderText>{sampleGroup.path}</PlaceholderText>
            </span>
          </p>
          <p className={`title-mark ${styles.title}`}>
            <PlaceholderText>{sampleGroup.name}</PlaceholderText>
          </p>
          <p className={styles.role}>
            <PlaceholderText>{t("myGroups.role.owner", { kind: t("myGroups.kind.department") })}</PlaceholderText>
          </p>
        </div>
      </div>

      {status ? (
        // The spend's line at its own height, with the status in it; an error is taller, and takes the room it needs.
        <div className={`${styles.amount} ${failed ? loadingStyles.amountSlot : loadingStyles.amountSlotFixed}`}>
          <div className={loadingStyles.status}>{status}</div>
        </div>
      ) : (
        <p className={styles.amount} aria-hidden="true">
          <PlaceholderText>$1,234.56</PlaceholderText>
        </p>
      )}
      <div className={`${styles.track} ${placeholder.pulse}`} aria-hidden="true" />

      {!dense && (
        <p className={mode === "loose" ? `${styles.summary} ${styles.summaryReserved}` : styles.summary} aria-hidden="true">
          <PlaceholderText>{t("myGroups.card.summary.onTrack", { projected: "$2,400", allowance: "$3,000" })}</PlaceholderText>
        </p>
      )}

      <dl className={styles.stats} aria-hidden="true">
        {[t("myGroups.card.projected"), t("myGroups.card.perPerson"), t("myGroups.card.activePeople")].map((label) => (
          <div key={label}>
            <dt>
              <PlaceholderText>{label}</PlaceholderText>
            </dt>
            <dd>
              <PlaceholderText>$1,234</PlaceholderText>
            </dd>
          </div>
        ))}
      </dl>

      {/* The month chart at the loaded chart's own height: 72px as a small trend in the dense grid, 220px otherwise. */}
      <div className={`${placeholder.block} ${dense ? loadingStyles.chartCompact : loadingStyles.chart}`} aria-hidden="true" />
      <div className={styles.tools} aria-hidden="true">
        <div className={`${styles.splitBar} ${placeholder.pulse}`} />
        <ul className={dense ? `${styles.toolList} ${styles.toolListCompact}` : styles.toolList}>
          {Array.from({ length: placeholderToolCount }, (_, index) => (
            <li key={index}>
              <span className={`${styles.swatch} ${placeholder.pulse} ${loadingStyles.swatch}`} />
              <span className={`${styles.toolIcon} ${placeholder.pulse} ${loadingStyles.swatch}`} />
              <span className={styles.toolName}>
                <PlaceholderText>{sampleGroup.tool}</PlaceholderText>
              </span>
              <span className={styles.toolAmount}>
                <PlaceholderText>{dense ? "75%" : "$1,717.50 · 75%"}</PlaceholderText>
              </span>
              {!dense && (
                <span className={styles.toolPeople}>
                  <PlaceholderText>{t("myGroups.card.toolPeople", { count: 4, total: 4 })}</PlaceholderText>
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* The single column also shows each day's spend by tool, a 150px chart under its title. */}
      {mode === "single" && (
        <div className={styles.daily} aria-hidden="true">
          <p className={styles.dailyTitle}>
            <PlaceholderText>{t("myGroups.card.dailyTitle")}</PlaceholderText>
          </p>
          <div className={`${placeholder.block} ${loadingStyles.dailyChart}`} />
        </div>
      )}
    </div>
  );
}

/**
 * "My Groups" while the groups load, in the layout the person chose: a row of group cards built from the same parts as
 * loaded ones, the first holding the spinner. When the groups could not be loaded, the same cards hold the error in its
 * place and stop pulsing, so a retry does not swap the page between two layouts.
 *
 * @param props.error - The error panel to show instead of the spinner, or nothing while loading.
 */
export function GroupsLoading({ error }: { error?: React.ReactNode }): React.JSX.Element {
  const { t } = useTranslation();
  const { mode } = useSectionLayout();
  const status = error ?? <LoadingState label={t("myGroups.loading")} />;

  return (
    <div aria-busy={error ? undefined : true} className={error ? placeholder.still : undefined}>
      <SectionGrid>
        {Array.from({ length: placeholderCardCount[mode] }, (_, index) => (
          <PlaceholderGroupCard key={index} mode={mode} status={index === 0 ? status : undefined} failed={error !== undefined} />
        ))}
      </SectionGrid>
    </div>
  );
}
